import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Reply, Trash2, Pin, MoreVertical, Send, Paperclip, Image as ImageIcon, Video, FileText, Mic, ChevronDown, Cpu, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatTimestamp } from '../utils/helpers';
import './ChatWindow.css';

const AVAILABLE_MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o', provider: 'OpenAI' },
  { id: 'gpt-4-turbo', name: 'GPT-4 Turbo', provider: 'OpenAI' },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo', provider: 'OpenAI' },
  { id: 'claude-3.5-sonnet', name: 'Claude 3.5 Sonnet', provider: 'Anthropic' },
  { id: 'claude-3-opus', name: 'Claude 3 Opus', provider: 'Anthropic' },
  { id: 'claude-3-haiku', name: 'Claude 3 Haiku', provider: 'Anthropic' },
  { id: 'mistral-large', name: 'Mistral Large', provider: 'Mistral AI' },
  { id: 'mistral-7b', name: 'Mistral 7B', provider: 'Mistral AI' },
  { id: 'llama-3-70b', name: 'Llama 3 70B', provider: 'Meta' },
  { id: 'gemini-pro', name: 'Gemini Pro', provider: 'Google' }
];

const ChatWindow = ({ 
  messages = [], 
  allMessages = [],
  agents,
  activeChat,
  isSending = false,
  onSendMessage, 
  onDeleteMessage,
  onReplyMessage,
  onPinMessage,
  onClearChat,
  selectedModel = 'gpt-4o',
  onModelChange,
  restoredScrollPosition = null,
  onSaveScroll,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [speechStatus, setSpeechStatus] = useState('');
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const memoryInputRef = useRef(null);
  const modelSelectorRef = useRef(null);
  const prevMessageCount = useRef(0);
  const scrollSaveTimer = useRef(null);
  const speechRecognitionRef = useRef(null);
  const speechBaseInputRef = useRef('');

  const resizeInput = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
    }
  };

  // Restore scroll position when chat changes, otherwise auto-scroll on new messages
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const currentCount = messages.length;
    const isNewMessage = currentCount > prevMessageCount.current;
    prevMessageCount.current = currentCount;

    if (isNewMessage) {
      // New message came in — scroll to bottom
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (restoredScrollPosition !== null && restoredScrollPosition > 0) {
      // Restoring from backend — jump to saved position
      container.scrollTop = restoredScrollPosition;
    } else if (currentCount > 0) {
      // Fresh chat switch with no saved position — go to bottom
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
      }, 50);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, activeChat?.id]);

  // Aggressive scroll to bottom during streaming
  useEffect(() => {
    if (isSending && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === 'agent' && lastMessage.streaming) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }
    }
  }, [messages, isSending]);

  // Throttled scroll saver
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !onSaveScroll) return;
    clearTimeout(scrollSaveTimer.current);
    scrollSaveTimer.current = setTimeout(() => {
      onSaveScroll(scrollContainerRef.current.scrollTop);
    }, 800);
  }, [onSaveScroll]);

  // Attach / detach scroll listener when activeChat changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollSaveTimer.current);
    };
  }, [activeChat?.id, handleScroll]);

  useEffect(() => {
    return () => {
      speechRecognitionRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    if (!showModelDropdown && !menuOpen && !headerMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        showModelDropdown &&
        modelSelectorRef.current &&
        !modelSelectorRef.current.contains(event.target)
      ) {
        setShowModelDropdown(false);
      }

      if (menuOpen && !event.target.closest('.message-menu-wrapper')) {
        setMenuOpen(null);
      }

      if (headerMenuOpen && !event.target.closest('.chat-header-menu-wrapper')) {
        setHeaderMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowModelDropdown(false);
        setMenuOpen(null);
        setHeaderMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModelDropdown, menuOpen, headerMenuOpen]);

  const handleSend = (e) => {
    e.preventDefault();
    if ((!inputValue.trim() && attachments.length === 0) || isSending) return;

    onSendMessage({
      content: inputValue,
      replyTo: replyingTo?.id,
      attachments
    });

    setInputValue('');
    setAttachments([]);
    setReplyingTo(null);
    setShowMentions(false);
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Auto-expand textarea
    resizeInput();

    // Check for @ mentions
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = value.slice(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionFilter(textAfterAt);
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const handleMentionSelect = (agent) => {
    const lastAtIndex = inputValue.lastIndexOf('@');
    const newValue = inputValue.slice(0, lastAtIndex) + `@${agent.name} `;
    setInputValue(newValue);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    setMenuOpen(null);
    inputRef.current?.focus();
  };

  const handleDelete = (messageId) => {
    onDeleteMessage(messageId);
    setMenuOpen(null);
  };

  const handlePin = (messageId) => {
    onPinMessage(messageId);
    setMenuOpen(null);
  };

  const handleClearChat = () => {
    onClearChat?.();
    setHeaderMenuOpen(false);
  };

  const handleCopy = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopiedMessageId(id);
    setTimeout(() => setCopiedMessageId(null), 2000);
    setMenuOpen(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSend(e);
    }
  };

  const handleAttachmentChange = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length > 0) {
      setAttachments((prev) => [...prev, ...selected]);
    }
    event.target.value = '';
  };

  const removeAttachment = (indexToRemove) => {
    setAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleVoiceInput = () => {
    if (isSending) return;

    if (isListening) {
      speechRecognitionRef.current?.stop();
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechStatus('Voice input is not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognition();
    speechRecognitionRef.current = recognition;
    speechBaseInputRef.current = inputValue.trimEnd();

    recognition.lang = navigator.language || 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
      setSpeechStatus('Listening...');
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript || '')
        .join(' ')
        .trim();
      const base = speechBaseInputRef.current;
      setInputValue(`${base}${base && transcript ? ' ' : ''}${transcript}`);
      requestAnimationFrame(resizeInput);
    };

    recognition.onerror = (event) => {
      const message = event.error === 'not-allowed'
        ? 'Microphone permission was denied.'
        : 'Voice input failed. Please try again.';
      setSpeechStatus(message);
    };

    recognition.onend = () => {
      setIsListening(false);
      speechRecognitionRef.current = null;
      setTimeout(() => {
        setSpeechStatus((current) => (current === 'Listening...' ? '' : current));
      }, 1200);
    };

    try {
      recognition.start();
    } catch {
      setIsListening(false);
      setSpeechStatus('Voice input could not start.');
    }
  };

  const getAgentById = (id) => agents.find(a => a.id === id);

  const filteredAgents = agents.filter(a => 
    a.name.toLowerCase().includes(mentionFilter.toLowerCase())
  );

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="active-chat-info">
          {activeChat ? (
            <>
              <div className="active-chat-avatar">
                {activeChat.type === 'agent' || activeChat.type === 'subChat' ? (
                  <span>{getAgentById(activeChat.type === 'subChat' ? activeChat.parentId : activeChat.id)?.avatar || '🤖'}</span>
                ) : (
                  <span>{activeChat.avatar || '👥'}</span>
                )}
              </div>
              <div className="active-chat-details">
                <h2 className="active-chat-name">
                  {activeChat.type === 'agent' ? (
                    getAgentById(activeChat.id)?.name
                  ) : activeChat.type === 'subChat' ? (
                    activeChat.name
                  ) : (
                    activeChat.name
                  )}
                </h2>
                <div className="active-chat-status">
                  {activeChat.type === 'agent' || activeChat.type === 'subChat' ? (
                    <><span className="status-indicator active"></span> Active now</>
                  ) : (
                    <>{activeChat.members?.length || 0} members</>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="no-chat-selected">Select a chat to start</div>
          )}
        </div>

        <div className="chat-header-actions">
          <div className="chat-header-menu-wrapper">
            <button
              type="button"
              className="header-action-button"
              onClick={() => setHeaderMenuOpen((open) => !open)}
              disabled={!activeChat}
              title="Chat options"
            >
              <MoreVertical size={20} />
            </button>

            {headerMenuOpen && activeChat && (
              <div className="chat-header-menu animate-slide-in-down">
                <button
                  type="button"
                  className="chat-header-menu-item danger"
                  onClick={handleClearChat}
                >
                  <Trash2 size={15} />
                  <span>Clear chat</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="messages-container" ref={scrollContainerRef}>
        {messages.length === 0 ? (
          <div className="empty-chat-state">
            <div className="empty-chat-icon">💬</div>
            <h3>No messages yet</h3>
            <p>Send a message to start the conversation.</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const isUser = message.sender === 'user';
            const agent = !isUser ? getAgentById(message.agentId) : null;
            
            return (
              <div 
                key={message.id} 
                className={`message-row ${isUser ? 'user' : 'agent'}`}
              >
                {!isUser && (
                  <div className="message-avatar">
                    {agent?.avatar || '🤖'}
                  </div>
                )}
                <div className="message-content-wrapper">
                  {!isUser && (
                    <div className="message-sender-name">
                      {agent?.name || 'Agent'}
                    </div>
                  )}
                  
                  {message.replyTo && (() => {
                    const messagesToSearch = allMessages.length > 0 ? allMessages : messages;
                    const repliedMessage = messagesToSearch.find(m => m.id === message.replyTo);
                    
                    if (!repliedMessage) return null;
                    
                    const repliedSender = repliedMessage.sender === 'user' 
                      ? 'You' 
                      : getAgentById(repliedMessage.agentId)?.name || 'Agent';
                    
                    return (
                      <div className="message-reply-context">
                        <div className="reply-context-content">
                          <div className="reply-context-header">
                            {repliedSender}
                          </div>
                          <div className="reply-context-text">
                            {repliedMessage.content}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="message-bubble-container">
                    <div className={`message-bubble ${message.isPinned ? 'pinned' : ''}`}>
                      {message.isPinned && (
                        <div className="pinned-indicator">
                          <Pin size={12} />
                        </div>
                      )}
                      <div className="message-text">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {message.content}
                        </ReactMarkdown>
                        {message.streaming && <span className="typing-cursor">|</span>}
                      </div>
                      <div className="message-timestamp">
                        {formatTimestamp(message.timestamp)}
                      </div>

                      <div className="message-actions">
                        <button 
                          className="message-action" 
                          onClick={() => handleReply(message)}
                          title="Reply"
                        >
                          <Reply size={14} />
                        </button>
                        <button 
                          className="message-action" 
                          onClick={() => handleCopy(message.content, message.id)}
                          title="Copy"
                        >
                          {copiedMessageId === message.id ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                        <button 
                          className="message-action" 
                          onClick={() => handlePin(message.id)}
                          title={message.isPinned ? "Unpin" : "Pin"}
                        >
                          <Pin size={14} className={message.isPinned ? 'pinned' : ''} />
                        </button>
                        <div className="message-menu-wrapper">
                          <button
                            className="message-action"
                            onClick={() => setMenuOpen(menuOpen === message.id ? null : message.id)}
                          >
                            <MoreVertical size={14} />
                          </button>

                          {menuOpen === message.id && (
                            <div className="message-menu animate-slide-in-up">
                              <button
                                className="message-menu-item danger"
                                onClick={() => handleDelete(message.id)}
                              >
                                <Trash2 size={14} />
                                <span>Delete Message</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="input-container">
        {replyingTo && (
          <div className="reply-bar">
            <div className="reply-bar-content">
              <Reply size={14} className="reply-icon" />
              <div className="reply-info">
                <div className="reply-header">
                  {replyingTo.sender === 'user' ? 'You' : getAgentById(replyingTo.agentId)?.name || 'Agent'}
                </div>
                <div className="reply-message-preview">
                  {replyingTo.content}
                </div>
              </div>
            </div>
            <button
              className="reply-cancel"
              onClick={() => setReplyingTo(null)}
            >
              ×
            </button>
          </div>
        )}

        <div className="input-wrapper">
          <div className="input-actions-left">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden-file-input"
              onChange={handleAttachmentChange}
            />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden-file-input"
              onChange={handleAttachmentChange}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              multiple
              className="hidden-file-input"
              onChange={handleAttachmentChange}
            />
            <input
              ref={memoryInputRef}
              type="file"
              accept=".txt,.md,.json,.csv,.pdf,.doc,.docx"
              multiple
              className="hidden-file-input"
              onChange={handleAttachmentChange}
            />
            <button type="button" className="input-action-button" title="Attach file" onClick={() => fileInputRef.current?.click()}>
              <Paperclip size={18} />
            </button>
            <button type="button" className="input-action-button" title="Add image" onClick={() => imageInputRef.current?.click()}>
              <ImageIcon size={18} />
            </button>
            <button type="button" className="input-action-button" title="Add video" onClick={() => videoInputRef.current?.click()}>
              <Video size={18} />
            </button>
            <button type="button" className="input-action-button" title="Add memory file" onClick={() => memoryInputRef.current?.click()}>
              <FileText size={18} />
            </button>
          </div>

          <form onSubmit={handleSend} className="input-form">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type a message... (@ to mention agents)"
              className="message-input"
              rows={1}
              disabled={isSending}
            />

            {showMentions && filteredAgents.length > 0 && (
              <div className="mentions-dropdown animate-slide-in-up">
                {filteredAgents.map(agent => (
                  <button
                    key={agent.id}
                    className="mention-item"
                    onClick={() => handleMentionSelect(agent)}
                    type="button"
                  >
                    <span className="mention-avatar">{agent.avatar}</span>
                    <span className="mention-name">{agent.name}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              type="submit"
              className="send-button"
              disabled={(!inputValue.trim() && attachments.length === 0) || isSending}
            >
              <Send size={18} />
            </button>
          </form>

          <button
            type="button"
            className={`input-action-button ${isListening ? 'listening' : ''}`}
            title={isListening ? 'Stop voice input' : 'Voice input'}
            onClick={handleVoiceInput}
            disabled={isSending}
          >
            <Mic size={18} />
          </button>

          <div className="model-selector-container" ref={modelSelectorRef}>
            <button 
              type="button"
              className="model-selector-button-input"
              onClick={() => setShowModelDropdown(!showModelDropdown)}
              title="Select AI Model"
            >
              <Cpu size={16} />
              <span className="model-selector-text">{AVAILABLE_MODELS.find(m => m.id === selectedModel)?.name || selectedModel}</span>
              <ChevronDown size={14} className={`model-dropdown-icon ${showModelDropdown ? 'open' : ''}`} />
            </button>

            {showModelDropdown && (
              <>
                <div className="model-dropdown-input animate-slide-in-down">
                  <div className="model-dropdown-header">
                    <Cpu size={14} />
                    <span>Select AI Model</span>
                  </div>
                  <div className="model-list">
                    {AVAILABLE_MODELS.map(model => (
                      <button
                        key={model.id}
                        className={`model-item ${selectedModel === model.id ? 'active' : ''}`}
                        onClick={() => {
                          onModelChange(model.id);
                          setShowModelDropdown(false);
                        }}
                        type="button"
                      >
                        <div className="model-item-info">
                          <span className="model-item-name">{model.name}</span>
                          <span className="model-item-provider">{model.provider}</span>
                        </div>
                        {selectedModel === model.id && (
                          <div className="model-item-check">✓</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {attachments.length > 0 && (
          <div className="attachment-list">
            {attachments.map((file, index) => (
              <div className="attachment-chip" key={`${file.name}-${file.lastModified}-${index}`}>
                <span className="attachment-name">{file.name}</span>
                <span className="attachment-size">{Math.ceil(file.size / 1024)} KB</span>
                <button type="button" className="attachment-remove" onClick={() => removeAttachment(index)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {speechStatus && (
          <div className={`voice-status ${isListening ? 'active' : ''}`}>
            {speechStatus}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatWindow;
